import { differenceInDays, addDays, isWeekend, max, min, parseISO } from 'date-fns';
import { Assignment, DailyHourOverride } from '@/components/assignments/types';

/**
 * Calculates the number of working hours for an assignment within a given time frame
 * Takes into account the daily hours setting and any hour overrides
 * @param assignment The assignment to calculate hours for
 * @param startDate Optional start date to constrain the calculation
 * @param endDate Optional end date to constrain the calculation
 * @param includeBillableOnly Whether to only include billable hours (defaults to false)
 */
export function getAssignmentWorkingHours(
  assignment: Assignment, 
  startDate?: Date, 
  endDate?: Date,
  includeBillableOnly: boolean = false
): number {
    console.log('⏱️ getAssignmentWorkingHours - Starting calculation:', {
      assignmentId: assignment.id,
      employee_id: assignment.employee_id,
      startDate: startDate,
      endDate: endDate,
      includeBillableOnly,
      dailyHours: assignment.daily_hours,
      overridesCount: assignment.hour_overrides?.length || 0
    });
    
    const assignmentStart = normalizeDate(assignment.start_date);
    const assignmentEnd = normalizeDate(assignment.end_date);
    
    if (!assignmentStart || !assignmentEnd) {
      console.log('⏱️ No valid assignment start/end dates - returning 0');
      return 0;
    }

    // If only billable hours should be counted and the assignment is not billable, return 0
    if (includeBillableOnly && assignment.isBillable === false) {
      console.log('⏱️ Assignment is not billable, but only counting billable hours - returning 0');
      return 0;
    }

    // Normalize and constrain dates
    const normalizedStartDate = startDate ? normalizeDate(startDate) : null;
    const normalizedEndDate = endDate ? normalizeDate(endDate) : null;
    
    const effectiveStartDate = normalizedStartDate ? max([assignmentStart, normalizedStartDate]) : assignmentStart;
    const effectiveEndDate = normalizedEndDate ? min([assignmentEnd, normalizedEndDate]) : assignmentEnd;
    
    console.log('⏱️ Effective date range:', {
      effectiveStartDate: effectiveStartDate,
      effectiveEndDate: effectiveEndDate
    });

    if (effectiveStartDate > effectiveEndDate) {
      console.log('⏱️ Invalid date range (start > end) - returning 0');
      return 0;
    }
    
    // Calculate base hours without overrides
    const defaultDailyHours = assignment.daily_hours || 8;
    console.log(`⏱️ Using default daily hours: ${defaultDailyHours}`);
    
    const baseHours = calculateBaseWorkingHours(
      effectiveStartDate, 
      effectiveEndDate, 
      defaultDailyHours
    );
    
    console.log(`⏱️ Base hours (without overrides): ${baseHours}`);
    
    // Apply hour overrides
    let finalHours = baseHours;
    
    if (assignment.hour_overrides && Array.isArray(assignment.hour_overrides)) {
      console.log(`⏱️ Processing ${assignment.hour_overrides.length} overrides`);
      
      assignment.hour_overrides.forEach((override, index) => {
        console.log(`⏱️ Override #${index + 1}:`, {
          id: override.id,
          start_date: override.start_date instanceof Date ? override.start_date.toISOString() : override.start_date,
          end_date: override.end_date instanceof Date ? override.end_date.toISOString() : override.end_date,
          daily_hours: override.daily_hours,
          isBillable: override.isBillable,
          description: override.description
        });
        
        // Skip non-billable overrides if we're only counting billable hours
        if (includeBillableOnly && override.isBillable === false) {
          console.log(`⏱️ Skipping non-billable override #${index + 1}`);
          return;
        }
        
        const overrideStart = normalizeDate(override.start_date);
        const overrideEnd = normalizeDate(override.end_date);
        
        // Skip invalid overrides
        if (!overrideStart || !overrideEnd || isNaN(overrideStart.getTime()) || isNaN(overrideEnd.getTime())) {
          console.warn(`⏱️ Skipping override #${index + 1} with invalid dates`);
          return;
        }
        
        // Find the overlapping period
        const overlapStart = max([effectiveStartDate, overrideStart]);
        const overlapEnd = min([effectiveEndDate, overrideEnd]);
        
        console.log(`⏱️ Override #${index + 1} overlap period:`, {
          overlapStart: overlapStart,
          overlapEnd: overlapEnd
        });
        
        // Skip if there's no overlap
        if (overlapStart > overlapEnd) {
          console.log(`⏱️ No date overlap for override #${index + 1} - skipping`);
          return;
        }
        
        // Calculate hours for this period with and without the override
        const periodBaseHours = calculateBaseWorkingHours(
          overlapStart, 
          overlapEnd, 
          defaultDailyHours
        );
        
        // For overrides, include weekend days
        const periodOverrideHours = calculateBaseWorkingHours(
          overlapStart, 
          overlapEnd, 
          override.daily_hours,
          false // Don't skip weekends for overrides
        );
        
        console.log(`⏱️ Override #${index + 1} calculation:`, {
          periodWithDefaultHours: periodBaseHours,
          periodWithOverrideHours: periodOverrideHours,
          difference: periodOverrideHours - periodBaseHours
        });
        
        // Apply the difference
        const oldFinalHours = finalHours;
        finalHours = finalHours - periodBaseHours + periodOverrideHours;
        
        console.log(`⏱️ Hours after applying override #${index + 1}: ${oldFinalHours} → ${finalHours}`);
      });
    } else {
      console.log('⏱️ No overrides to process');
    }
    
    console.log(`⏱️ Final calculated hours: ${finalHours}`);
    return finalHours;
}

/**
 * Calculate working hours for a date range with a fixed daily hours value
 * (No overrides, just plain calculation)
 * @param skipWeekends Whether to skip weekend days (default: true)
 */
function calculateBaseWorkingHours(
  startDate: Date, 
  endDate: Date, 
  dailyHours: number, 
  skipWeekends: boolean = true
): number {
  const totalDays = differenceInDays(endDate, startDate) + 1;
  let workingHours = 0;
  let workingDays = 0;
  
  for (let i = 0; i < totalDays; i++) {
    const currentDate = addDays(startDate, i);
    
    // Skip weekends if specified
    if (skipWeekends && isWeekend(currentDate)) {
      continue;
    }
    
    workingHours += dailyHours;
    workingDays++;
  }
  
  console.log(`⏱️ calculateBaseWorkingHours: ${workingDays} working days × ${dailyHours} hours = ${workingHours} hours${skipWeekends ? ' (excluding weekends)' : ' (including weekends)'}`);
  return workingHours;
}

/**
 * Normalize date by removing time component or converting strings to Date objects
 */
function normalizeDate(date: Date): Date {
  // Strip time component
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  
}
